import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { isNil, isEmpty } from "ramda";

import { useAlgoliaActions } from "store/AlgoliaSlice";
import { SORT_ITEMS } from "enums";
import { jjLogger } from "utils/logUtils";
import UserPresenter from "presenters/UserPresenter";
import { ALGOLIA_FILTERS } from "enums/algoliaFilters";

import useUsers from "./useUsers";

const useAlgolia = () => {
  const { currentUser } = useUsers();
  const {
    addOneFilter,
    removeOneFilter,
    clearFilter,
    clearAllFilters,
    clearAllFiltersByPath,
    setIsClearAllClicked,
    setItemToRemove,
    setSortFilterSelected,
    setNumberOfPeopleFilters,
    setContentTypesFilters,
  } = useAlgoliaActions();
  const {
    castNumFilters,
    templateGroupTypeFilters,
    isClearAllClicked,
    itemToRemove,
    sortFilterSelected,
    numberOfPeopleFilters,
    contentTypesFilters,
  } = useSelector((state) => state.AlgoliaSlice);

  const isDefaultSort = sortFilterSelected?.label === SORT_ITEMS.bestMatch.label;

  const getUserToken = (sendTokenOnlyForPaidUser) => {
    let userToken;

    if (sendTokenOnlyForPaidUser) {
      if (UserPresenter.isPaid(currentUser)) userToken = UserPresenter.id(currentUser);
    } else {
      userToken = UserPresenter.id(currentUser);
      if (!userToken) {
        try {
          window.aa("getUserToken", null, (err, newUserToken) => {
            if (err) {
              jjLogger.log(`useAlgoliaEvents.js - getUserToken() - Error while generating newUserToken - ${err}`);
              return;
            }
            userToken = newUserToken;
          });
        } catch (error) {
          jjLogger.log(`useAlgoliaEvents.js - getUserToken() - Error calling aa - ${error}`);
        }
      }
    }
    return userToken;
  };

  const contentTypeFilterValues = useMemo(() => {
    return (path) => {
      return !contentTypesFilters[path]?.length ? [] : [contentTypesFilters[path].flatMap((item) => item.value)];
    };
  }, [contentTypesFilters]);

  const numberOfPeopleFilterValues = useMemo(() => {
    return (path) => {
      return !numberOfPeopleFilters[path]?.length
        ? []
        : [numberOfPeopleFilters[path].map((item) => `${ALGOLIA_FILTERS.castNumOptions}:${item}`)];
    };
  }, [numberOfPeopleFilters]);

  const filterValues = useMemo(() => {
    return (path) => {
      const contentTypeFiltersForPath = contentTypeFilterValues(path);
      const numberOfPeopleFiltersForPath = numberOfPeopleFilterValues(path);
      const sortFilterSelectedForPath = sortFilterSelected[path]?.value;

      return {
        contentTypeFiltersForPath,
        numberOfPeopleFiltersForPath,
        sortFilterSelectedForPath,
      };
    };
  }, [contentTypeFilterValues, numberOfPeopleFilterValues, sortFilterSelected]);

  const hasFacetFilterValues = useMemo(() => {
    return (path) => {
      const { contentTypeFiltersForPath, numberOfPeopleFiltersForPath } = filterValues(path);
      return (
        !isEmpty(contentTypeFiltersForPath) &&
        !isNil(contentTypeFiltersForPath) &&
        !isEmpty(numberOfPeopleFiltersForPath) &&
        !isNil(numberOfPeopleFiltersForPath)
      );
    };
  }, [filterValues]);

  const DEFAULT_CONFIGURE_UI_STATE = {
    clickAnalytics: true,
    maxValuesPerFacet: 10,
    distinct: true,
    analytics: true,
    hitsPerPage: 30,
  };

  const createUiState = useCallback(
    (config) => {
      if (!config || typeof config !== "object" || !config.query) return {};

      const { query, limit, sortBy } = config;
      const { query: queryValue, facetFilters } = query;
      const { sendTokenOnlyForPaidUser, ...configure } = config?.configure ?? {};

      const userToken = getUserToken(sendTokenOnlyForPaidUser ?? false);

      const state = {
        query: queryValue,
        configure: {
          ...DEFAULT_CONFIGURE_UI_STATE,
          hitsPerPage: limit ?? DEFAULT_CONFIGURE_UI_STATE.hitsPerPage,
          facetFilters,
          ...configure,
          userToken,
        },
      };

      if (sortBy) state.sortBy = sortBy;

      return state;
    },
    [currentUser],
  );

  const createInitialUiStateFromSingle = useCallback(
    (indexName, config) => {
      if (!indexName || isNil(config) || isEmpty(config)) return undefined;

      const uiState = createUiState(config);

      return isEmpty(uiState) || isNil(uiState) ? undefined : { [indexName]: uiState };
    },
    [createUiState],
  );

  const createInitialUiStateFromMultiple = useCallback(
    (configs) => {
      if (!configs?.length) return undefined;

      return configs.reduce((acc, config) => {
        const moduleUiState = createInitialUiStateFromSingle(config.id, config);
        return {
          ...acc,
          ...(moduleUiState || {}),
        };
      }, {});
    },
    [createInitialUiStateFromSingle],
  );

  return {
    addOneFilter,
    removeOneFilter,
    clearFilter,
    clearAllFilters,
    clearAllFiltersByPath,
    castNumFilters,
    templateGroupTypeFilters,
    isClearAllClicked,
    setIsClearAllClicked,
    itemToRemove,
    setItemToRemove,
    setSortFilterSelected,
    sortFilterSelected,
    isDefaultSort,
    getUserToken,
    setNumberOfPeopleFilters,
    numberOfPeopleFilters,
    setContentTypesFilters,
    contentTypesFilters,
    filterValues,
    hasFacetFilterValues,
    createInitialUiStateFromMultiple,
    createInitialUiStateFromSingle,
    createUiState,
    DEFAULT_CONFIGURE_UI_STATE,
  };
};

export default useAlgolia;
